import { FC, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { useForm } from '@carcareers/contexts';

interface DateInputProps {
  name: string;
  label: string;
  minDate?: Dayjs;
  hasTime?: boolean;
}

export const DateInput: FC<DateInputProps> = ({ name, label, minDate, hasTime = false }) => {
  const { getData, setData, getErrors } = useForm();
  const [date, setDate] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    const today = dayjs();
    const storedDate = getData(name);

    try {
      if (storedDate && (typeof storedDate === 'string' || storedDate instanceof Date)) {
        const parsed = dayjs(storedDate);
        if (parsed.isValid()) {
          setDate(parsed);
        } else {
          setDate(today);
          setData(name, today.toISOString());
        }
      } else {
        setDate(today);
        setData(name, today.toISOString());
      }
    } catch (error) {
      console.error('Error parsing date value:', error);
      setDate(today);
      setData(name, today.toISOString());
    }
  }, [name]);

  const handleDateChange = (date: Dayjs | null) => {
    if (date && date.isValid()) {
      setDate(date);
      setData(name, date.toISOString());
    }
  };

  const commonProps = {
    label,
    value: date,
    onChange: handleDateChange,
    sx: { width: '100%' },
    slotProps: {
      textField: {
        error: !!getErrors(name),
        helperText: getErrors(name)
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {hasTime ? (
        <DateTimePicker {...commonProps} minDate={minDate} />
      ) : (
        <DatePicker {...commonProps} minDate={minDate} />
      )}
    </LocalizationProvider>
  );
};

export default DateInput;
